import type { UserProfile, UserTimeZone } from '@skychat/shared-core';

export const DEFAULT_PROFILE_TIME_ZONE = 'Europe/Rome';

const FALLBACK_TIME_ZONES = [
  'UTC', 'Europe/Rome', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
  'Europe/Athens', 'Europe/Moscow', 'Africa/Cairo', 'Africa/Johannesburg', 'America/New_York',
  'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Toronto',
  'America/Mexico_City', 'America/Sao_Paulo', 'Pacific/Rarotonga', 'Asia/Dubai', 'Asia/Kolkata',
  'Asia/Bangkok', 'Asia/Singapore', 'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Tokyo', 'Asia/Seoul',
  'Australia/Perth', 'Australia/Sydney', 'Pacific/Auckland'
];

export function isValidTimeZone(timeZone: unknown): timeZone is string {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: String(timeZone) }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}

export function profileTimeZoneId(profile?: Partial<UserProfile> | null): string {
  const nested = profile?.timeZone as UserTimeZone | undefined;
  const candidate = String(profile?.time_zone_id || nested?.id || DEFAULT_PROFILE_TIME_ZONE);
  const updatedAt = Number(profile?.time_zone_updated_at ?? nested?.updatedAt ?? 0);
  if ((candidate === 'UTC' || candidate === 'Etc/UTC') && updatedAt <= 0) return DEFAULT_PROFILE_TIME_ZONE;
  return isValidTimeZone(candidate) ? candidate : DEFAULT_PROFILE_TIME_ZONE;
}

export function timeZoneCityLabel(timeZone: string): string {
  const normalized = isValidTimeZone(timeZone) ? timeZone : DEFAULT_PROFILE_TIME_ZONE;
  if (normalized === 'UTC' || normalized === 'Etc/UTC') return 'UTC';
  return (normalized.split('/').pop() || normalized).replace(/_/g, ' ');
}

export function timeZoneOffsetMinutes(timeZone: string, date = new Date()): number {
  const normalized = isValidTimeZone(timeZone) ? timeZone : DEFAULT_PROFILE_TIME_ZONE;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: normalized,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  const zonedAsUtc = Date.UTC(
    Number(values.year), Number(values.month) - 1, Number(values.day),
    Number(values.hour), Number(values.minute), Number(values.second)
  );
  return Math.round((zonedAsUtc - date.getTime()) / 60000);
}

export function formatTimeZoneClock(timeZone: string, date = new Date(), locale?: string): string {
  const normalized = isValidTimeZone(timeZone) ? timeZone : DEFAULT_PROFILE_TIME_ZONE;
  const time = new Intl.DateTimeFormat(locale, {
    timeZone: normalized,
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).format(date);
  return `${timeZoneCityLabel(normalized)} ${time}`;
}

export function peerTimeZoneClock(
  me?: Partial<UserProfile> | null,
  peer?: Partial<UserProfile> | null,
  date = new Date(),
  locale?: string
) {
  const ownTimeZone = profileTimeZoneId(me);
  const peerTimeZone = profileTimeZoneId(peer);
  return {
    visible: timeZoneOffsetMinutes(ownTimeZone, date) !== timeZoneOffsetMinutes(peerTimeZone, date),
    ownTimeZone,
    peerTimeZone,
    label: formatTimeZoneClock(peerTimeZone, date, locale)
  };
}

export function supportedProfileTimeZones(): string[] {
  let timeZones = FALLBACK_TIME_ZONES;
  try {
    const supportedValuesOf = (Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] }).supportedValuesOf;
    if (typeof supportedValuesOf === 'function') timeZones = supportedValuesOf('timeZone');
  } catch {
    // Older WebViews use the curated fallback list.
  }
  return Array.from(new Set([DEFAULT_PROFILE_TIME_ZONE, 'UTC', ...timeZones])).filter(isValidTimeZone);
}

export function timeZoneSelectLabel(timeZone: string): string {
  const city = timeZoneCityLabel(timeZone);
  return city === timeZone ? city : `${city} — ${timeZone}`;
}

export function profileTimeZonePatch(timeZone: UserTimeZone): Partial<UserProfile> {
  return {
    time_zone_mode: timeZone.mode,
    time_zone_id: timeZone.id,
    time_zone_display_name: timeZone.displayName,
    time_zone_offset_minutes: timeZone.offsetMinutes,
    time_zone_updated_at: timeZone.updatedAt,
    timeZone
  };
}
